<template>
  <div>
    <div class="white-section mt-1" style="padding: 10px 20px">
      <div class="d-flex justify-content-end" style="align-items: center">
        <div style="margin-left: auto" class="page-title">
          <b-breadcrumb-item
            v-for="item in breadcrumb"
            :key="item.text"
            :active="item.active"
            class="bread-crumb"
          >
          <div v-if="item.active">
              {{ item.translate ? $t(`navigation.${item.text}`) : item.text }}
          </div>
            <!-- {{$route.meta}} -->
            <!-- {{ item.text }} -->
          </b-breadcrumb-item>
        </div>
        <div
          class="custom-search mb-0 d-flex"
          style="box-shadow: none; padding: 10px 0"
        >
          <b-form-group>
            <div class="d-flex align-items-center">
              <label class="mr-1">{{ $t("general.search") }}</label>
              <b-form-input
                v-model="searchTerm"
                :placeholder="$t('general.search')"
                type="text"
                class="d-inline-block"
              />
            </div>
          </b-form-group>
        </div>
        <router-link
          :disabled="
            !$ability.can('create', 'medicalSpecialties') &&
            !$ability.can('read', 'superuser')
          "
          tag="button"
          :to="{ name: 'addMedicalSpecialties' }"
          class="btn btn-success btn-add"
          >{{ $t("general.add") }}</router-link
        >
      </div>
      <!-- table -->
      <vue-good-table
        class="user-table"
        :columns="columns"
        :rows="resMedicalSpecialties"
        :search-options="{
          enabled: true,
          externalQuery: searchTerm,
        }"
        :select-options="{
          enabled: true,
          selectOnCheckboxOnly: true, // only select when checkbox is clicked instead of the row
          selectionInfoClass: 'custom-class',
          selectionText: 'rows selected',
          clearSelectionText: 'clear',
          disableSelectInfo: true, // disable the select info panel on top
          selectAllByGroup: true, // when used in combination with a grouped table, add a checkbox in the header row to check/uncheck the entire group
        }"
        :pagination-options="{
          enabled: true,
          perPage: pageLength,
        }"
      >
        <!-- Slot: Table Column -->
        <template slot="table-column" slot-scope="props">
          <span v-if="props.column.label === 'Status'" class="text-nowrap">
            {{ $t("message.tableHeader.status") }}
          </span>
          <span v-else-if="props.column.label === 'Action'" class="text-nowrap">
            {{ $t("message.tableHeader.action") }}
          </span>
          <span v-else>
            {{ props.column.label }}
          </span>
        </template>

        <!-- Slot: Table Row -->
        <template slot="table-row" slot-scope="props">
          <!-- Column: Action -->
          <span v-if="props.column.field === 'action'">
            <span>
              <b-dropdown
                variant="link"
                toggle-class="text-decoration-none"
                no-caret
              >
                <template v-slot:button-content>
                  <feather-icon
                    icon="MoreVerticalIcon"
                    size="16"
                    class="text-body align-middle mr-25"
                  />
                </template>
                <b-dropdown-item
                  :disabled="
                    !$ability.can('update', 'medicalSpecialties') &&
                    !$ability.can('read', 'superuser')
                  "
                  @click="editMedicalSpecialty(props.row)"
                >
                  <feather-icon icon="Edit2Icon" class="mr-50" />
                  <span>{{ $t("general.edit") }}</span>
                </b-dropdown-item>
                <b-dropdown-item
                  :disabled="
                    !$ability.can('delete', 'medicalSpecialties') &&
                    !$ability.can('read', 'superuser')
                  "
                  @click="removeMedicalSpecialty(props.row)"
                >
                  <feather-icon icon="TrashIcon" class="mr-50" />
                  <span>{{ $t("general.delete") }}</span>
                </b-dropdown-item>
              </b-dropdown>
              <b-form-checkbox
                @input="switchStatus($event, props.row)"
                switch
                inline
                :checked="props.row.status === 'active'"
              >
                {{ $t(`general.${props.row.status}`) }}
              </b-form-checkbox>
            </span>
          </span>
          <!-- Column: Common -->
          <span v-else>
            {{ props.formattedRow[props.column.field] }}
          </span>
        </template>

        <!-- pagination -->
        <template slot="pagination-bottom" slot-scope="props">
          <div class="d-flex justify-content-between flex-wrap">
            <div class="d-flex align-items-center mb-0 mt-1">
              <span class="text-nowrap">
                {{ $t("general.pageLength") }}
              </span>
              <b-form-select
                v-model="pageLength"
                :options="['20', '50', '100']"
                class="mx-1"
                @change="handlePageChange"
              />
              <span class="text-nowrap">
                {{ $t("general.of") }} {{ total }} {{ $t("general.pageText2") }}
              </span>
            </div>
            <div>
              <b-pagination
                v-model="page"
                :total-rows="total"
                :per-page="pageLength"
                first-number
                last-number
                align="right"
                prev-class="prev-item"
                next-class="next-item"
                class="mt-1 mb-0"
                @change="handlePageChange"
              >
                <template #prev-text>
                  <feather-icon icon="ChevronLeftIcon" size="18" />
                </template>
                <template #next-text>
                  <feather-icon icon="ChevronRightIcon" size="18" />
                </template>
              </b-pagination>
            </div>
          </div>
        </template>
      </vue-good-table>
    </div>
  </div>
</template>

<script>
import {
  BBreadcrumb,
  BBreadcrumbItem,
  BRow,
  BCol,
  BButton,
} from "bootstrap-vue";
import Ripple from "vue-ripple-directive";
import { mapGetters } from "vuex";
import {
  BAvatar,
  BBadge,
  BPagination,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BAlert,
  BDropdown,
  BDropdownItem,
  BFormCheckbox,
} from "bootstrap-vue";
import { VueGoodTable } from "vue-good-table";
import store from "@/store";

export default {
  components: {
    BBreadcrumb,
    BBreadcrumbItem,
    VueGoodTable,
    BAvatar,
    BBadge,
    BPagination,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BAlert,
    BDropdown,
    BDropdownItem,
    BFormCheckbox,
  },
  data() {
    return {
      pageLength: 100,
      page: 1,
      dir: false,
      columns: [
        {
          label: this.$t("medicalSpecialties.medicalSpecialtiesTitle"),
          field: "title",
        },
        {
          label: this.$t("general.action"),
          field: "action",
        },
      ],
      searchTerm: "",
      resMedicalSpecialties:''
    };
  },
  watch: {
    page() {
        this.$store.dispatch("medicalSpecialties/getMedicalSpecialties", {
          page: parseInt(this.page),
          limit: parseInt(this.pageLength),
        }).then((res) => {
        this.resMedicalSpecialties=res
        this.total=res.total
      });
      
    },
    pageLength() {
      this.page=1
        this.$store.dispatch("medicalSpecialties/getMedicalSpecialties", {
          page: parseInt(this.page),
          limit: parseInt(this.pageLength),
        }).then((res) => {
        this.resMedicalSpecialties=res
        this.total=res.total
      });
    },
  },
  methods: {
    switchStatus(val, props) {
      val === true ? (props.status = "active") : (props.status = "inactive");
      let payload = {
        status: props.status,
      };
      this.$store
        .dispatch("medicalSpecialties/updateMedicalSpecialty", {
          id: props._id,
          payload: payload,
        })
        .then(() => {})
        .catch((error) => {});
    },

    removeItem(index) {
      this.resMedicalSpecialties.splice(index, 1);
    },
    removeMedicalSpecialty(row) {
      this.$bvModal
        .msgBoxConfirm(this.$t("general.confirmDelete"), {
          title: this.$t("general.confirmDeleteTitle"),
          size: "sm",
          buttonSize: "sm",
          okVariant: "danger",
          okTitle: this.$t("general.yes"),
          cancelTitle: this.$t("general.no"),
          footerClass: "p-2",
          hideHeaderClose: false,
          centered: true,
        })
        .then((value) => {
          console.log(value , 'value')
          if (value === true) {
            this.$store.commit("SET_IS_LOADING", true);
            this.$store
              .dispatch("medicalSpecialties/deleteMedicalSpecialty", {
                medicalSpecialtiesId: row._id,
              })
              .then(() => {
                this.$bvToast.toast(this.$t("toastification.deletionSucceed"), {
                  title: this.$t("toastification.success"),
                  variant: "success",
                  solid: true,
                });
                this.removeItem(row.originalIndex);
                this.$store.commit("SET_IS_LOADING", false);
              })
              .catch((error) => {
                let msg = "";
                switch (error.response.status) {
                  case 422:
                    msg = this.$t("toastification.deletionFailed");
                    break;

                  case 404:
                    msg = this.$t("toastification.deletionFailed");
                    break;

                  case 400:
                    msg = this.$t("toastification.badRequest");
                    break;
                }

                this.$bvToast.toast(msg, {
                  title: this.$t("toastification.error"),
                  variant: "danger",
                  solid: true,
                });
                this.$store.commit("SET_IS_LOADING", false);
              });
          }
        })
        .catch((err) => {
          alert("error");
        });
    },
    editMedicalSpecialty(medicalSpecialties) {
      // this.$store.commit(
      //   "medicalSpecialties/SET_MEDICAL_SPECIALTY_TO_EDIT",
      //   medicalSpecialties
      // );
      this.$router.push({
        name: "editMedicalSpecialties",
        params: { id: medicalSpecialties._id },
      });
    },
    // searchMedicalSpecialty(val) {
    //   if (val.length) {
    //     this.$store.dispatch("medicalSpecialties/searchMedicalSpecialty", {
    //       title: val,
    //       page: parseInt(this.page),
    //       limit: parseInt(this.pageLength),
    //     })
    //   }
    // },
    handlePageChange() {
      document.body.scrollTop = 0;
      document.documentElement.scrollTop = 0;
    },
  },
  computed: {
    breadcrumb() {
      return typeof this.$route.meta.breadCrumb === "function"
        ? this.$route.meta.breadCrumb(this.$route)
        : this.$route.meta.breadCrumb;
    },
    ...mapGetters({
      medicalSpecialties: "medicalSpecialties/medicalSpecialties",
    }),

    statusVariant() {
      const statusColor = {
        /* eslint-disable key-spacing */
        Current: "light-primary",
        Professional: "light-success",
        Rejected: "light-danger",
        Resigned: "light-warning",
        Applied: "light-info",
        /* eslint-enable key-spacing */
      };

      return (status) => statusColor[status];
    },
    total: {
      get() {
        return this.$store.getters["medicalSpecialties/total"];
      },
    },
  },
  created() {
    this.$store.commit("SET_IS_LOADING", true);
    this.$store
      .dispatch("medicalSpecialties/getMedicalSpecialties", {
        page: parseInt(this.page),
          limit: parseInt(this.pageLength),
      })
      .then((res) => {
        this.resMedicalSpecialties=res
        this.total=res.total
        this.$store.commit("SET_IS_LOADING", false);
      });
    this.setBreadcrumbs([
      { text: "homePage", translate: true, to: { name: "home" } },
      { text: "baseInformation", translate: true },
      {
        text: "medicalSpecialties",
        to: { name: "manageMedicalSpecialties" },
        translate: true,
      },
    ]);
    this.setCurrentBreadcrumb([
      {
        text: "medicalSpecialties",
        to: { name: "medicalSpecialties" },
        translate: true,
      },
    ]);
  },
};
</script>
